import { useState, useEffect } from 'react';
import { ChevronRight, Search, Book } from 'lucide-react';

interface SurahMeta {
  number: number;
  name: string;
  englishName: string;
  englishNameTranslation: string;
  numberOfAyahs: number;
  revelationType: string;
}

interface QuranIndexProps {
  onSelectSurah: (id: number) => void;
  onBack: () => void;
}

export function QuranIndex({ onSelectSurah, onBack }: QuranIndexProps) {
  const [surahs, setSurahs] = useState<SurahMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetch('https://api.alquran.cloud/v1/surah')
      .then(res => res.json())
      .then(data => {
        setSurahs(data.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch Surah list', err);
        setLoading(false);
      });
  }, []);

  const filteredSurahs = surahs.filter(s => 
    s.englishName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.englishNameTranslation.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.number.toString().includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col pb-8">
      {/* Header */}
      <div className="bg-white px-6 py-6 pt-safe-top border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={onBack}
            className="p-2 -ml-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
          >
            <ChevronRight className="w-6 h-6 rotate-180" />
          </button>
          <div className="text-center">
            <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
              The Noble Quran <Book className="w-5 h-5 text-emerald-600" />
            </h2>
            <p className="text-sm text-slate-500">Read in the name of your Lord</p>
          </div>
          <div className="w-10"></div>
        </div>

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search Surah (e.g. Yasin, 36)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-100 pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading Surahs...</div>
        ) : (
          filteredSurahs.map(surah => (
            <div 
              key={surah.number}
              onClick={() => onSelectSurah(surah.number)}
              className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex items-center justify-between hover:border-emerald-200 hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center font-bold text-emerald-700 text-sm group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  {surah.number}
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 group-hover:text-emerald-700 transition-colors">
                    {surah.englishName}
                  </h3>
                  <p className="text-xs text-slate-500">{surah.englishNameTranslation} • {surah.numberOfAyahs} Verses</p>
                </div>
              </div>
              <div className="text-right">
                <span className="font-serif text-xl text-slate-400 group-hover:text-slate-800 transition-colors">
                  {surah.name.replace('سورة ', '')}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
